import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2, Tv, ShieldCheck, Zap, Rocket, Star, Info } from "lucide-react";
import { motion } from "framer-motion";

export const Route = createFileRoute("/tutoriais-iptv/samsung")({
  head: () => ({
    meta: [
      { title: "IPTV na Samsung: Guia Completo para Instalar Aplicativos" },
      { name: "description", content: "Aprenda como configurar IPTV na sua Smart TV Samsung. Guia completo de instalação de aplicativos e como trabalhar com Painel IPTV Pós Pago." },
    ],
  }),
  component: SamsungTutorial,
});

function SamsungTutorial() {
  const apps = [
    { name: "One X", desc: "Aplicativo próprio com interface moderna e excelente desempenho." },
    { name: "P2PRO", desc: "Desenvolvido para oferecer estabilidade e facilidade de uso absoluta." },
    { name: "P2PLAY", desc: "Solução otimizada para clientes que procuram praticidade extrema." },
    { name: "Prime Play", desc: "Compatível com Samsung Smart TV e diversos outros dispositivos." },
    { name: "Blessed", desc: "Interface moderna, minimalista e muito simples de configurar." },
    { name: "Fun Play", desc: "Excelente alternativa leve para Smart TVs Samsung." },
    { name: "Lazer Play", desc: "Aplicativo extremamente leve e intuitivo para o dia a dia." },
    { name: "VuPlayer", desc: "Compatível com múltiplas plataformas e Smart TVs Samsung." },
    { name: "IBO Player", desc: "Um dos aplicativos mais populares e estáveis do mercado." }
  ];

  const servers = [
    { name: "P2PRO", desc: "Estabilidade premium para revendedores em crescimento.", icon: <ShieldCheck className="w-6 h-6" /> },
    { name: "One X", desc: "Aplicativo próprio e exclusivo integrado ao sistema.", icon: <Zap className="w-6 h-6" /> },
    { name: "P2PLAY", desc: "Estrutura moderna para múltiplos dispositivos.", icon: <Tv className="w-6 h-6" /> },
    { name: "Prime Play", desc: "Solução completa com vasto catálogo de conteúdos.", icon: <Star className="w-6 h-6" /> }
  ];

  return (
    <SEOPage
      title="IPTV na Samsung: Guia Completo para Instalar Aplicativos"
      subtitle="As Smart TVs Samsung são referência em desempenho para IPTV. Aprenda a configurar sua estrutura profissional hoje."
      category="Tutoriais Samsung"
      introParagraphs={[
        "As Smart TVs Samsung estão entre os dispositivos mais utilizados pelos clientes de IPTV devido à facilidade de instalação de aplicativos, excelente desempenho e interface intuitiva que a marca oferece.",
        "Se você procura uma solução profissional para revenda, é fundamental utilizar aplicativos compatíveis com um Painel IPTV Pós Pago estável e preparado para crescimento em larga escala."
      ]}
      breadcrumbs={[
        { label: "Início", href: "/" },
        { label: "Tutoriais", href: "/tutoriais-iptv" },
        { label: "Samsung", href: "/tutoriais-iptv/samsung" }
      ]}
      benefits={[
        { title: "Instalação Simples", description: "Apps disponíveis diretamente na loja oficial da Samsung." },
        { title: "Alta Performance", description: "Processamento rápido para streaming em 4K sem travamentos." },
        { title: "Interface Amigável", description: "Navegação fluida através do controle remoto da TV." },
        { title: "Variedade de Apps", description: "Suporte para os melhores players do mercado IPTV." },
        { title: "Gestão Pós-Paga", description: "Controle total do seu negócio com custo reduzido." },
        { title: "Suporte Dedicado", description: "Auxílio técnico para configuração em qualquer modelo." }
      ]}
      howItWorks={[
        { title: "Loja de Apps", description: "Acesse o menu de aplicativos da sua Samsung Smart TV." },
        { title: "Busca e Instalação", description: "Pesquise pelo player desejado e clique em instalar." },
        { title: "Configuração", description: "Abra o app e insira os dados do seu Painel Pós Pago." },
        { title: "Sucesso", description: "Aproveite a melhor estabilidade de sinal do mercado." }
      ]}
      diferenciais={[
        { title: "Menor Investimento", description: "No modelo pós-pago, você paga apenas pelo que consome." },
        { title: "Ativação Instantânea", description: "Liberação de sinal em segundos após o cadastro." },
        { title: "Escalabilidade", description: "Cresça sua base de clientes sem limites técnicos." },
        { title: "Margem de Lucro", description: "A melhor rentabilidade para revendedores profissionais." }
      ]}
      faq={[
        { question: "Qual o melhor aplicativo IPTV para Samsung?", answer: "One X, P2PRO, Prime Play e P2PLAY estão entre as principais opções compatíveis e recomendadas por nossa equipe." },
        { question: "Posso revender IPTV utilizando Smart TV Samsung?", answer: "Com certeza. Os aplicativos disponíveis na Samsung Store oferecem excelente estabilidade para revenda." },
        { question: "O Painel IPTV Pós Pago vale a pena?", answer: "Sim. É o modelo de negócio mais seguro e lucrativo para quem está começando ou já possui escala." },
        { question: "Quais servidores vocês oferecem suporte?", answer: "Trabalhamos com os melhores: P2PRO, One X, P2PLAY e Prime Play." }
      ]}
      sections={[
        {
          title: "Aplicativos Recomendados para Samsung",
          type: "grid",
          content: (
            <>
              {apps.map((app, i) => (
                <motion.div 
                  key={i}
                  whileHover={{ scale: 1.02 }}
                  className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all"
                >
                  <h3 className="text-lg font-bold text-electric mb-2">{app.name}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{app.desc}</p>
                </motion.div>
              ))}
            </>
          )
        },
        {
          title: "Servidores de Alta Estabilidade",
          type: "grid",
          content: (
            <>
              {servers.map((server, i) => (
                <div key={i} className="p-8 rounded-3xl bg-electric/5 border border-electric/10 flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-electric/20 flex items-center justify-center text-electric mb-4">
                    {server.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{server.name}</h3>
                  <p className="text-muted-foreground text-sm">{server.desc}</p>
                </div>
              ))}
            </>
          )
        },
        {
          title: "Como Iniciar Sua Revenda Profissional",
          type: "featured",
          content: (
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-electric text-white w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold">1</div>
                  <div>
                    <h4 className="font-bold mb-1">Solicite seu Teste</h4>
                    <p className="text-muted-foreground text-sm">Fale com nossa equipe e conheça a interface do painel.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-electric text-white w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold">2</div>
                  <div>
                    <h4 className="font-bold mb-1">Acesso ao Painel</h4>
                    <p className="text-muted-foreground text-sm">Receba suas credenciais e configure sua conta.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-electric text-white w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold">3</div>
                  <div>
                    <h4 className="font-bold mb-1">Venda e Lucre</h4>
                    <p className="text-muted-foreground text-sm">Cadastre seus clientes e utilize nossos apps premium.</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/5 p-8 rounded-[2rem] border border-white/10 italic text-sm text-muted-foreground">
                <Info className="w-6 h-6 text-electric mb-4" />
                "O mercado de IPTV na Samsung é um dos mais lucrativos. A facilidade do cliente final em baixar o app reduz drasticamente o tempo de suporte do revendedor."
              </div>
            </div>
          )
        }
      ]}
      ctaTitle="🚀 Torne-se um Revendedor IPTV Hoje Mesmo"
      ctaDescription="Tenha acesso a uma estrutura profissional completa, com os melhores servidores e suporte especializado para crescer seu negócio."
    />
  );
}
